// src/components/guides/QuickGuideLayouts.jsx
import React from 'react';
import { motion } from 'framer-motion';
import {
  Copy,
  CheckCircle,
  ChevronRight,
  AlertCircle,
  Star,
  Zap,
  Target,
  Clock,
  Heart,
  Shield,
  Brain
} from 'lucide-react';
import Card from '../shared/Card';

// ==========================================
// BLOCKS LAYOUT - For listening blocks
// ==========================================
export const BlocksLayout = ({ content }) => (
  <div className="space-y-4">
    {content.blocks.map((block, idx) => (
      <Card key={idx} className="p-4">
        <h4 className="text-lg font-semibold text-white mb-3">{block.name}</h4>
        {block.tagline && (
          <p className="text-sm text-purple-300 italic mb-3">"{block.tagline}"</p>
        )}
        
        <div className="space-y-3">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Signs:</p>
            <ul className="space-y-1">
              {block.signs.map((sign, sidx) => (
                <li key={sidx} className="text-sm text-slate-300 flex items-start gap-2">
                  <span className="text-red-400 mt-0.5">•</span>
                  {sign}
                </li>
              ))}
            </ul>
          </div>
          
          {block.inAction && (
            <div className="p-3 bg-red-500/10 rounded">
              <p className="text-sm text-red-300">
                <strong>In Action:</strong> {block.inAction}
              </p>
            </div>
          )}
          
          <div className="p-3 bg-green-500/10 rounded">
            <p className="text-sm text-green-300">
              <strong>Quick Fix:</strong> {block.quickFix}
            </p>
          </div>
          
          {block.deeperTruth && (
            <p className="text-xs text-slate-400 italic">
              💭 {block.deeperTruth}
            </p>
          )}
        </div>
      </Card>
    ))}
  </div>
);

// ==========================================
// STEPS LAYOUT - For HEAR technique and similar
// ==========================================
export const StepsLayout = ({ content }) => (
  <div className="space-y-4">
    {content.steps.map((step, idx) => (
      <Card key={idx} className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-lg">{step.letter}</span>
          </div>
          <div className="flex-1">
            <h4 className="text-white font-semibold text-lg mb-1">{step.word}</h4>
            <p className="text-slate-300 mb-3">{step.action}</p>
            
            {step.details && (
              <ul className="space-y-1 mb-3">
                {step.details.map((detail, didx) => (
                  <li key={didx} className="text-sm text-slate-400 flex items-start gap-2">
                    <ChevronRight className="w-3 h-3 text-purple-400 mt-1 flex-shrink-0" />
                    {detail}
                  </li>
                ))}
              </ul>
            )}
            
            {step.example && (
              <div className="p-3 bg-blue-500/10 rounded mb-2">
                <p className="text-sm text-blue-300">
                  <strong>Example:</strong> {step.example}
                </p>
              </div>
            )}
            
            {step.whyItWorks && (
              <p className="text-xs text-slate-500 italic">
                💡 {step.whyItWorks}
              </p>
            )}
          </div>
        </div>
      </Card>
    ))}
    
    {content.proTip && (
      <Card className="p-4 bg-purple-500/10 border-purple-500/20">
        <p className="text-purple-300">
          <Zap className="w-4 h-4 inline mr-1" />
          <strong>Pro Tip:</strong> {content.proTip}
        </p>
      </Card>
    )}
    
    {content.practice && (
      <Card className="p-4 bg-green-500/10 border-green-500/20">
        <p className="text-green-300">
          <Target className="w-4 h-4 inline mr-1" />
          <strong>Practice:</strong> {content.practice}
        </p>
      </Card>
    )}
    
    {content.warning && (
      <Card className="p-4 bg-yellow-500/10 border-yellow-500/20">
        <p className="text-yellow-300">
          <AlertCircle className="w-4 h-4 inline mr-1" />
          <strong>Warning:</strong> {content.warning}
        </p>
      </Card>
    )}
  </div>
);

// AnchorsLayout - Navigation-based content with anchor links
export const AnchorsLayout = ({ content }) => (
  <div className="space-y-6">
    {content.anchors && (
      <div className="flex flex-wrap gap-2 pb-4 border-b border-gold/20">
        {content.anchors.map((anchor, index) => (
          <button
            key={index}
            onClick={() => {
              const element = document.getElementById(`anchor-${index}`);
              element?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-3 py-1 bg-gold/20 hover:bg-gold/30 text-pearl rounded-full text-sm transition-colors"
          >
            {anchor.label}
          </button>
        ))}
      </div>
    )}
    
    {content.sections?.map((section, index) => (
      <div key={index} id={`anchor-${index}`} className="scroll-mt-4">
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-bright-gold mb-3">
            {section.title}
          </h3>
          <div className="space-y-2 text-pearl/90">
            {section.content}
          </div>
        </Card>
      </div>
    ))}
  </div>
);

// TypesLayout - For different types or categories
export const TypesLayout = ({ content }) => (
  <div className="space-y-4">
    {content.types?.map((type, index) => (
      <Card key={index} className="p-4">
        <h3 className="text-white font-semibold text-lg mb-2">{type.name}</h3>

        {type.purpose && (
          <div className="mb-3">
            <p className="text-xs text-purple-400 uppercase tracking-wider mb-1">Purpose</p>
            <p className="text-slate-300 text-sm">{type.purpose}</p>
          </div>
        )}

        {type.description && (
          <p className="text-slate-300 mb-2">{type.description}</p>
        )}

        {type.why && (
          <div className="mb-3 p-3 bg-blue-500/10 rounded">
            <p className="text-blue-300 text-sm">
              <strong>Why it works:</strong> {type.why}
            </p>
          </div>
        )}

        {type.examples && Array.isArray(type.examples) && (
          <div className="mb-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Examples</p>
            <div className="space-y-1">
              {type.examples.map((example, idx) => (
                <p key={idx} className="text-sm text-slate-400 pl-3 border-l-2 border-purple-500/30">
                  {example}
                </p>
              ))}
            </div>
          </div>
        )}

        {type.example && !Array.isArray(type.examples) && (
          <p className="text-slate-400 text-sm italic mb-2">
            Example: {type.example}
          </p>
        )}

        {type.magic && (
          <div className="p-3 bg-purple-500/10 rounded mb-2">
            <p className="text-purple-300 text-sm">
              <Zap className="w-3 h-3 inline mr-1" />
              <strong>The Magic:</strong> {type.magic}
            </p>
          </div>
        )}

        {type.avoid && (
          <div className="p-3 bg-yellow-500/10 rounded">
            <p className="text-yellow-300 text-sm">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              <strong>Avoid:</strong> {type.avoid}
            </p>
          </div>
        )}
      </Card>
    ))}
  </div>
);

// TechniquesLayout - For conversation techniques
export const TechniquesLayout = ({ content }) => (
  <div className="space-y-4">
    {content.techniques?.map((technique, index) => (
      <Card key={index} className="p-4">
        <h3 className="text-bright-gold font-semibold mb-2">{technique.name}</h3>
        <p className="text-pearl/90 mb-3">{technique.description}</p>
        {technique.steps && (
          <ol className="space-y-1 text-sm text-pearl/80">
            {technique.steps.map((step, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-gold">{i + 1}.</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        )}
      </Card>
    ))}
  </div>
);

// LadderLayout - Progressive skill levels
export const LadderLayout = ({ content }) => (
  <div className="space-y-4">
    {content.levels?.map((level, index) => (
      <Card key={index} className={`p-4 ${index === 0 ? 'border-green-500/30' : index === content.levels.length - 1 ? 'border-gold/30' : ''}`}>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-2xl">{level.icon || '🪜'}</span>
          <h3 className="text-gold font-semibold">{level.name}</h3>
        </div>
        <p className="text-pearl/80">{level.description}</p>
      </Card>
    ))}
  </div>
);

// HacksLayout - Quick tips and hacks
export const HacksLayout = ({ content }) => (
  <div className="space-y-4">
    {content.hacks?.map((hack, index) => (
      <Card key={index} className="p-4">
        <h3 className="text-white font-semibold text-lg mb-2">{hack.name || hack.title}</h3>

        {hack.move && (
          <div className="mb-3 p-3 bg-red-500/10 rounded">
            <p className="text-red-300 text-sm">
              <strong>Filler Words:</strong> {hack.move}
            </p>
          </div>
        )}

        {hack.why && (
          <div className="mb-3">
            <p className="text-slate-300 text-sm">{hack.why}</p>
          </div>
        )}

        {hack.bonus && (
          <div className="mb-3 p-3 bg-green-500/10 rounded">
            <p className="text-green-300 text-sm">
              <strong>The Fix:</strong> {hack.bonus}
            </p>
          </div>
        )}

        {hack.warning && (
          <div className="p-3 bg-yellow-500/10 rounded">
            <p className="text-yellow-300 text-sm">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              {hack.warning}
            </p>
          </div>
        )}

        {hack.tip && !hack.name && (
          <p className="text-slate-300 text-sm">{hack.tip}</p>
        )}
      </Card>
    ))}
  </div>
);

// CategoriesLayout - Organized by categories
export const CategoriesLayout = ({ content }) => {
  // Handle both array format and object format
  if (content.categories && typeof content.categories === 'object' && !Array.isArray(content.categories)) {
    // Object format (like flow-states guide)
    return (
      <div className="space-y-6">
        {Object.entries(content.categories).map(([key, category]) => (
          <Card key={key} className="p-4">
            <h3 className="text-white font-semibold text-lg mb-2">{category.title}</h3>

            {category.questions && Array.isArray(category.questions) && (
              <ul className="space-y-2 mb-3">
                {category.questions.map((question, idx) => (
                  <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">•</span>
                    {question}
                  </li>
                ))}
              </ul>
            )}

            {category.items && Array.isArray(category.items) && (
              <ul className="space-y-2 mb-3">
                {category.items.map((item, idx) => (
                  <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            )}

            {category.why && (
              <div className="p-3 bg-blue-500/10 rounded">
                <p className="text-blue-300 text-sm">
                  <strong>Why:</strong> {category.why}
                </p>
              </div>
            )}
          </Card>
        ))}
      </div>
    );
  }

  // Array format (original)
  return (
    <div className="space-y-6">
      {content.categories?.map((category, index) => (
        <div key={index}>
          <h3 className="text-white font-semibold mb-3">{category.name}</h3>
          <div className="space-y-2">
            {category.items?.map((item, i) => (
              <Card key={i} className="p-3">
                <p className="text-slate-300">{item}</p>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// LevelsLayout - Difficulty or skill levels
export const LevelsLayout = ({ content }) => (
  <div className="space-y-4">
    {content.levels?.map((level, index) => (
      <Card key={index} className="p-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-gold font-semibold">{level.name}</h3>
          <span className="text-xs px-2 py-1 bg-gold/20 rounded-full text-pearl">
            Level {index + 1}
          </span>
        </div>
        <p className="text-pearl/80">{level.description}</p>
      </Card>
    ))}
  </div>
);

// TransitionsLayout - For conversation transitions
export const TransitionsLayout = ({ content }) => (
  <div className="space-y-4">
    {content.transitions?.map((transition, index) => (
      <Card key={index} className="p-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-conversation-blue">→</span>
          <h3 className="text-gold font-medium">{transition.from} to {transition.to}</h3>
        </div>
        <p className="text-pearl/80 mb-2">{transition.phrase}</p>
        {transition.example && (
          <p className="text-conversation-blue/80 text-sm italic">
            "{transition.example}"
          </p>
        )}
      </Card>
    ))}
  </div>
);

// ExitsLayout - For ending conversations
export const ExitsLayout = ({ content }) => (
  <div className="space-y-4">
    {content.exits?.map((exit, index) => (
      <Card key={index} className="p-4">
        <h3 className="text-gold font-semibold mb-2">{exit.situation}</h3>
        <p className="text-pearl/90 mb-2">{exit.phrase}</p>
        <p className="text-pearl/60 text-sm">
          Follow-up: {exit.followUp}
        </p>
      </Card>
    ))}
  </div>
);

// FormulaLayout - Step-by-step formulas
export const FormulaLayout = ({ content }) => (
  <div className="space-y-4">
    {content.formula && (
      <Card className="p-4 bg-gradient-luxury text-charcoal">
        <p className="font-semibold text-center">{content.formula}</p>
      </Card>
    )}
    {content.steps?.map((step, index) => (
      <Card key={index} className="p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-gold rounded-full flex items-center justify-center text-black font-bold">
            {index + 1}
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-pearl mb-1">{step.title}</h4>
            <p className="text-pearl/80 text-sm">{step.description}</p>
          </div>
        </div>
      </Card>
    ))}
  </div>
);

// ==========================================
// EXPORT ALL LAYOUTS AS DEFAULT
// ==========================================
const QuickGuideLayouts = {
  BlocksLayout,
  StepsLayout,
  TypesLayout,
  TechniquesLayout,
  LadderLayout,
  HacksLayout,
  CategoriesLayout,
  LevelsLayout,
  AnchorsLayout,
  TransitionsLayout,
  ExitsLayout,
  FormulaLayout
};

export default QuickGuideLayouts;